import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { AssetsDataService } from '@services/assets-data.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { OrganisationDataService, IOrganisation, Organisation, OrganisationDataModelNode, DataModelNodeRequest, OrganisationsHierarchy } from '@services/organisation-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { DragAndDropEventArgs, NodeExpandEventArgs, NodeSelectEventArgs, TreeViewComponent } from '@syncfusion/ej2-angular-navigations';
import { DataPoint } from '@services/datapoints-data.service';
import { MatDialog } from '@angular/material/dialog';
import { AddDataModelNodeComponent, AddDataModelNodeDialogData } from 'src/app/dialogs/add-data-model-node/add-data-model-node.component';
import { IIntegratonSensor, IntegrationSensorsService } from '@services/integration-sensors.service';

export class TreeDashboardDrag {
	htmlElement: HTMLElement;
	panelID: string;
	draggedNode: OrganisationDataModelNode | any;
}

@Component({
	selector: 'app-organisation-data-model',
	templateUrl: './organisation-data-model.component.html',
	styleUrls: ['./organisation-data-model.component.scss']
})
export class OrganisationDataModelComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** organisationDataModel */
	public organisationDataModel: OrganisationDataModelNode[] = [];

	/** field */
	public fields: Object

	/** treeview */
	@ViewChild('treeview') treeview!: TreeViewComponent;

	/** selectedNode */
	public selectedNode?: OrganisationDataModelNode | any;

	/** flattenedDataModel */
	public flattenedDataModel: OrganisationDataModelNode[] = [];

	/** sensors */
	public sensors: IIntegratonSensor[] = [];

	/** onDashboardManagementPage */
	@Input() onDashboardManagementPage: boolean = false;

	/** onReportingPage */
	@Input() onReportingPage: boolean = false;

	/** onDialog */
	@Input() onDialog: boolean = false;

	/** outputDataModel */
	@Input() outputDataModel: boolean = false;

	/** dragComplete */
	@Output() dragComplete = new EventEmitter<TreeDashboardDrag>();

	/** dataModel */
	@Output() dataModel = new EventEmitter<OrganisationDataModelNode[]>();

	/** expandedNodes */
	private expandedNodes: { nodeID: string, expanded: boolean }[] = [];

	/** firstFetch */
	private firstFetch: boolean = true;

	/**
	 * Default Constructor
	 * @param auth auth 
	 * @param organisationDataService organisationDataService  
	 * @param globalFunctions globalFunctions 
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param assetsDataService assetsDataService 
	 * @param document document 
	 */
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		private integrationSensorsService: IntegrationSensorsService,
		public dialog: MatDialog,
		@Inject(DOCUMENT) public document: Document,
	) {

		/** Get required subscriptions */
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, selectedAccount, selectedOrganisationUID, paramMap]) =>
				!!currentUser && !!selectedAccount && !!selectedOrganisationUID && paramMap.has('organisation'))
		).subscribe({
			next: ([currentUser, selectedAccount, selectedOrganisation, paramMap]) => {
				this.thisUser = currentUser

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.accountGUID = this.selectedAccount.GUID;

				this.organisation = <IOrganisation>selectedOrganisation;
				this.organisationUID = this.organisation.Uid;

				let urlAccountID = this.router.url.split('/')[1];
				this.getOrganisationDataModel();

				this.GetSensors(this.accountGUID, this.organisationUID, '');
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getOrganisationDataModel
	 */
	getOrganisationDataModel() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.getOrganisationDataModel$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((model) => !!model),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.updateTree();
			})
		).subscribe({
			next: (model) => {
				this.organisationDataModel = JSON.parse(model);
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.getOrganisationDataModel$:", error);
				}
			},
		});
	}

	/**
	 * Update Tree
	 */
	updateTree() {
		this.organisationDataModel = this.addDatapointsAsSubchildren(this.organisationDataModel);
		this.organisationDataModel = this.addOrganisationToTopLevel(this.organisationDataModel);

		if (this.outputDataModel) {
			this.dataModel.emit(this.organisationDataModel);
		}

		this.flattenedDataModel = this.flattenNodes(this.organisationDataModel, true);
		if (this.firstFetch) {
			this.firstFetch = false;
			this.expandedNodes = this.flattenedDataModel
				.map((x: OrganisationDataModelNode) => (
					{
						nodeID: x.DataModelNodeUID,
						expanded: ['organisation', 'folder'].includes(x.NodeType) ? false : true
					}));
		}

		this.organisationDataModel = this.resetExpanded(this.organisationDataModel);

		this.organisationDataModel[0].Expanded = true;

		this.fields = {
			dataSource: this.organisationDataModel,
			id: 'DataModelNodeUID',
			text: 'Name',
			child: 'SubChild',
			// iconCss: 'LocationType',
			// imageUrl: 'ImageURL',
			expanded: "Expanded",
			// hidden: 'Hidden',
			nodeType: "NodeType",
			parentUID: 'ParentNodeUID'
		};

		if (this.treeview) {
			this.treeview.dataBind();
			this.treeview.refresh();
		}
	}

	/** checkNodeExpanded */
	checkNodeExpanded(nodeUID: string): boolean {
		const data = this.expandedNodes.find((x) => x.nodeID == nodeUID);
		return data ? data.expanded : false;
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * selectNode
	 * @param args args  
	 */
	selectNode(args: NodeSelectEventArgs) {
		let findNode = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == args.nodeData["id"]);
		if (findNode) {
			this.selectedNode = findNode;
		}
	}

	/**
	 * getNodeTitle
	 * @param node node 
	 * @returns 
	 */
	getNodeTitle(node: OrganisationDataModelNode): string {
		return `${node.NodeType.replace(node.NodeType[0], node.NodeType[0].toUpperCase())}: ${node.Name}`;
	}

	/**
	 * addDatapointsAsSubchildren
	 * @param model model 
	 * @returns 
	 */
	addDatapointsAsSubchildren(model: OrganisationDataModelNode[]): OrganisationDataModelNode[] {
		model.forEach((x: OrganisationDataModelNode) => {
			if (x.NodeType == "sensor" && (x.Datapoints?.length ?? 0) > 0) {
				x.SubChild = x.Datapoints;
				x.SubChild?.forEach((y) => {
					y["DataModelNodeUID"] = y.DataPointID.toString();
					y["Name"] = y.DPTitle;
					y["Description"] = "";
					y["NodeType"] = "datapoint";
					y["NodeLevel"] = x.NodeLevel + 1;
					y["ParentUID"] = x.DataModelNodeUID;
					y["DataModelNodeID"] = y.DataPointID;
				});
			}

			if (x.SubChild && x.SubChild.length > 0) {
				this.addDatapointsAsSubchildren(x.SubChild);
			}
		})
		return model;
	}

	/**
	 * addOrganisationToTopLevel
	 * @param model model 
	 * @returns 
	 */
	addOrganisationToTopLevel(model: OrganisationDataModelNode[]): OrganisationDataModelNode[] {
		let organisaionNode: any = { ...this.organisation };
		organisaionNode["DataModelNodeUID"] = organisaionNode.Uid;
		organisaionNode["NodeType"] = "organisation";
		organisaionNode["NodeLevel"] = 0;
		organisaionNode["DataModelNodeID"] = organisaionNode.Id;
		organisaionNode["SubChild"] = [...model];
		return [organisaionNode];
	}

	/**
	 * openAddChildNodeDialog
	 * @param parent parent 
	 */
	openAddChildNodeDialog(parent: OrganisationDataModelNode) {
		const NodeTypes: { text: string, value: number }[] = [
			{ text: "folder", value: 0 },
			{ text: "sensor", value: 1 }
		];

		const data: AddDataModelNodeDialogData = {
			ParentNode: parent,
			NodeTypes: NodeTypes,
			Sensors: this.sensors
		}

		const dialogRef = this.dialog.open(AddDataModelNodeComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			if (result.NodeType == 1) {
				result.SensorNodes.forEach((sensorNode: { name: string, description: string, datapointIDs: string }) => {
					this.addChildNode(parent, NodeTypes[result.NodeType].text, sensorNode.name, sensorNode.description, sensorNode.datapointIDs);
				});
			} else {
				this.addChildNode(parent, NodeTypes[result.NodeType].text, result.Name, result.Description ?? "", result.DatapointIDs ?? "");
			}
		});
	}

	/**
	 * addChildNode
	 * @param parent parent  
	 */
	addChildNode(parent: OrganisationDataModelNode, nodeType: string, name: string, description: string, datapointIDs: string) {
		let data: DataModelNodeRequest = {
			ParentNodeUID: parent.NodeLevel != 0 ? parent.DataModelNodeUID : "",
			NodeType: nodeType,
			Name: name,
			Description: description,
			DatapointIDs: datapointIDs
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.postOrganisationDataModeNode$(this.accountGUID, this.organisationUID, data).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((node) => !!node),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (node) => {
				this.getOrganisationDataModel();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.postOrganisationDataModeNode$:", error);
				}
			},
		});
	}

	/**
	 * GetSensors
	 * @param selectedAccountGUID
	 * @param routeOrganisationID
	 * @param routeWebhookID
	 */
	GetSensors(selectedAccountGUID: string, routeOrganisationID: string, routeWebhookID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);
		this.integrationSensorsService.getIntegrationSensors$(selectedAccountGUID, routeOrganisationID, routeWebhookID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((sensors) => !!sensors),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (sensors) => {
				if (sensors) {
					this.sensors = <IIntegratonSensor[]>sensors;
					this.sensors = this.sensors.filter((x: IIntegratonSensor) => x.Name != "");
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "GetSensors Error: this.integrationSensorsService.getIntegrationSensors$", false)
			},
			complete: () => { this.globalFunctions.displaySpinner(false) }
		})
	}

	/**
	 * deleteOrganisationDataModelNode
	 * @param node node 
	 */
	deleteOrganisationDataModelNode(node: OrganisationDataModelNode) {
		this.globalFunctions.UserConfirm$(`Delete ${node.NodeType.replace(node.NodeType[0], node.NodeType[0].toUpperCase())} ${node.Name}?`).subscribe(OKResult => {
			if (OKResult) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				this.organisationDataService.deleteOrganisationDataModelNode$(this.accountGUID, this.organisationUID, node.DataModelNodeUID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((node) => !!node),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						this.getOrganisationDataModel();
						this.globalFunctions.DisplaySnackBar({
							message: response["Result"] == "Error" ? response["Error"] : response["Result"].replace("OK -", ""),
							duration: 2000,
							action: ""
						});
						this.selectedNode = undefined;
					},
					error: (error) => {
						if (error.status != 404) {
							console.error("this.organisationDataService.deleteOrganisationDataModelNode$:", error);
						}
					},
				});
			}
		});
	}

	/**
	 * onDragStop -- dragging onto a dashboard
	 * @param event event  
	 */
	onDragStop(event: DragAndDropEventArgs) {
		let elements: any[] = [];
		let target: HTMLElement | null = event.target;
		while (target) {
			elements.push(target);
			target = target.parentElement;
		}
		let targetElement: HTMLElement = elements.find((x: HTMLElement) => x.classList.contains("panel-host"));
		const draggedNode: OrganisationDataModelNode | undefined = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == event.draggedNodeData["id"]);
		if (targetElement && draggedNode) {
			this.dragComplete.emit({
				htmlElement: targetElement,
				panelID: targetElement.id,
				draggedNode: draggedNode
			});
		}
	}

	/**
	 * onDragStopReport -- Dragging into a report
	 * @param event event 
	 */
	onDragStopReport(event: DragAndDropEventArgs) {
		let elements: any[] = [];
		let target: HTMLElement | null = event.target;
		while (target) {
			elements.push(target);
			target = target.parentElement;
		}
		let targetElement: HTMLElement = elements.find((x: HTMLElement) => x.classList.contains("drag-target"));
		const draggedNode: OrganisationDataModelNode | undefined = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == event.draggedNodeData["id"]);
		if (targetElement && draggedNode) {
			this.dragComplete.emit({
				htmlElement: targetElement,
				panelID: targetElement.id,
				draggedNode: draggedNode
			});
		}
	}

	/**
	 * onDragIntoTree
	 * @param event event 
	 */
	onDragIntoTree(event: DragAndDropEventArgs) {
		const droppedNode = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == event.droppedNodeData['id']);
		let draggedNode = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == event.draggedNodeData['id']);
		let valid: boolean = false;
		if (draggedNode && droppedNode) {
			if ((draggedNode.DataModelNodeUID != droppedNode.DataModelNodeUID)
				&& (droppedNode.DataModelNodeUID != draggedNode.ParentNodeUID)) {
				if (["organisation", "folder"].includes(droppedNode.NodeType)) {
					valid = true;
				}
			}
			if (valid) {
				draggedNode.ParentNodeUID = droppedNode.DataModelNodeUID;
				this.patchOrganisationDataModelNode(draggedNode);
			} else {
				event.cancel = true;
			}
		}
	}

	/**
	 * patchOrganisationDataModelNode
	 * @param node node 
	 */
	patchOrganisationDataModelNode(node: OrganisationDataModelNode) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.patchOrganisationDataModelNode$(this.accountGUID, this.organisationUID, node.DataModelNodeUID, node).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((node) => !!node),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.getOrganisationDataModel();
				this.globalFunctions.DisplaySnackBar({
					message: response["Result"] == "Error" ? response["Error"] : response["Result"].replace("OK -", ""),
					duration: 2000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.patchOrganisationDataModelNode$:", error);
				}
			},
		});
	}

	/**
	 * Set the expanded state of the nodes according to the stored data
	 * @param data
	 * @returns
	 */
	resetExpanded(data: OrganisationDataModelNode[]): OrganisationDataModelNode[] {

		/**
		 * Internal Function
		 * @param data
		 */
		const transformListItems = (data: OrganisationDataModelNode[]) => {
			for (let i = 0; i < data.length; i++) {
				try {
					let node = this.expandedNodes.find((x) => x.nodeID == data[i].DataModelNodeUID);
					if (node) {
						if (!data[i].SubChild && !data[i].Datapoints) {
							data[i].Expanded = false
						} else {
							data[i].Expanded = node["expanded"]
						}
					} else {
						data[i].Expanded = false
					}
					if (data[i].SubChild) {
						transformListItems(data[i].SubChild!);
					}
				} catch (error) {
					console.error(error);
				}
			}
		};
		transformListItems(data);
		return data;
	}

	/**
	 * nodeExpanded
	 * @param event event  
	 */
	nodeExpanded(event: NodeExpandEventArgs) {
		let data = this.expandedNodes.find((x) => x.nodeID == event.nodeData['id']);
		if (data) {
			data.expanded = <boolean>event.nodeData['expanded'];
		}
	}
}
